import axios from 'axios';
import { get } from 'svelte/store';
import { api } from './api.js';
import { config, subscriptionConfig } from '../../config/client.js';
import {
  enhanceQueryWithContext,
  generateAdvancedAnswer,
  generateBasicAnswer,
  generateHistoryTitle,
  generateProAnswer
} from './aiService.js';
import { getCurrentLanguage, translateFromEnglish, translateToEnglish } from './translation/translationService.js';
import { updateStores } from './storeService.js';
// Update imports: remove generateDateRangeString, add monthsToFreshness
// Remove calculateRelevance from the import below
import { buildSiteRestrictedQuery, simplifyQuery, monthsToFreshness } from './utils.js';
import { logTotalCost } from './CostTracker.js';
//import 'toastr/build/toastr.min.css'; // Import toastr CSS
import {
  followUpAnswer,
  followUpIsLoadingAnswer,
  followUpIsLoadingQueryHeader,
  followUpIsLoadingResults,
  followUpQueries,
  followUpResults,
  followUpResultsVisible,
  history,
  isHistoryLoading,
  isLoadingAnswer,
  isLoadingResults,
  overallSummary,
  queryHeader,
  results,
  resultsVisible,
  user as userStore, // alias user store to avoid conflict if 'user' variable is used locally
  userTier
} from '../stores/stores.js';

// Initialize the follow-up arrays
followUpQueries.set([]);
followUpIsLoadingResults.set([]);
followUpIsLoadingAnswer.set([]);
followUpResults.set([]);
followUpResultsVisible.set([]);
followUpAnswer.set([]);

// ------------------------------
// Utility: log cost info received from LLM-orchestration API responses
// ------------------------------
function logCostFromLLMResponse(resp) {
  // Accepts the raw object returned by aiService.* helpers
  if (!resp) return;

  // Different server versions may call this block 'costDetails', 'usageData', or 'usage'
  const costData = resp.costDetails || resp.usageData || resp.usage;
  if (!costData) return;

  // Normalise to the simple {inputTokens, outputTokens} shape expected by logTotalCost
  const inputTokens = costData.totalInputTokens ?? costData.inputTokens ?? 0;
  const outputTokens = costData.totalOutputTokens ?? costData.outputTokens ?? 0;
  if (inputTokens === 0 && outputTokens === 0) return;

  // Provider / model fallbacks (now from server response)
  const provider = resp.provider || 'openai';
  const model = resp.model || 'gpt-4o-mini';

  logTotalCost({ inputTokens, outputTokens }, `${provider}:${model}`);
}

export async function performSearch(query, passedUserId, searchType = 'basic') {
  let userIdToUse = passedUserId;

  // Fallback to localStorage only if passedUserId is not provided
  if (!userIdToUse) {
    const storedData = localStorage.getItem('user');
    if (storedData) {
      try {
        const data = JSON.parse(storedData);
        userIdToUse = data.userId;
        console.log('[performSearch] Using userId from localStorage as fallback:', userIdToUse);
      } catch (e) {
        console.error('[performSearch] Failed to parse user from localStorage:', e);
      }
    }
  }

  if (!userIdToUse && process.env.NODE_ENV === 'development') {
    console.warn('[performSearch] userId is not available. User-specific features like usage tracking and history saving will be skipped. This might be due to mock authentication setup.');
  } else if (!userIdToUse) {
    console.error('[performSearch] CRITICAL: userId is not available. User-specific features will fail or be skipped.');
    // In a production scenario, you might want to throw an error here or prevent the search.
  }
  
  console.log(`[performSearch] Effective userId for API calls: ${userIdToUse}, Query: "${query}", Type: ${searchType}`);

  try {
    // Setting initial states
    queryHeader.set(query);
    isLoadingResults.set(true);
    isLoadingAnswer.set(true);
    results.set([]);
    overallSummary.set('');
    resultsVisible.set(false);

    // Perform user-specific checks only if userIdToUse is available
    if (userIdToUse) {
      await CheckUserUsage(userIdToUse); // This calls /api/search/userTotalUsage
      await getUserUsage(userIdToUse, searchType); // This calls /api/search/userUsage
      console.log(`[performSearch] User usage checks completed for userId: ${userIdToUse}`);
    } else {
      console.warn("[performSearch] Skipping user usage checks as userId is not available.");
    }

    const currentLanguage = getCurrentLanguage();
    let englishQuery = query;

    // Translation if needed
    if (currentLanguage !== 'en') {
      try {
        console.log(`Translating query from ${currentLanguage} to English`);
        englishQuery = await translateToEnglish(query, currentLanguage);
        console.log("Translated query:", englishQuery);
      } catch (translationError) {
        console.error("Translation failed, proceeding with original query:", query);
        englishQuery = query; // Use original query if translation fails
      }
    }

    const enhancedQuery =
      englishQuery.trim().length > 0 && englishQuery !== '' && false
        ? await enhanceQueryWithContext(englishQuery, '', '')
        : englishQuery;
    const simplifiedQuery = simplifyQuery(enhancedQuery);
    const siteRestrictedQuery = buildSiteRestrictedQuery(simplifiedQuery);
    
    const searchTypeSettings = subscriptionConfig.searchTypes[searchType];
    const freshness = monthsToFreshness(12);
    const bingParams = {
      q: siteRestrictedQuery,
      count: searchTypeSettings.maxSearchResults
    };
    if (freshness) bingParams.freshness = freshness;

    console.groupCollapsed(`🔍 TRACE for query: ${query}`);
    console.log({ englishQuery, enhancedQuery, simplifiedQuery, siteRestrictedQuery });
    console.log('Final Bing q param ➜', bingParams.q);
    console.groupEnd();

    console.log('Bing Request Params (performSearch):', bingParams);

    const response = await api.post('/api/search', bingParams);

    //EXA.AI IMPLEMENTATION
    if (response.data.results && Array.isArray(response.data.results)) {
      const searchResults = response.data.results;
      const pageData = searchResults.map(result => ({
        title: result.title,                                   // titlul paginii
        snippet: result.text 
                  ? result.text.slice(0, 280)                  // text curățat (trunchiat la 280 chars)
                  : result.summary || "",                      // fallback pe summary
        url: result.url                                       // link-ul original
      }));
    

    /* if (response.data.webPages && response.data.webPages.value) {
      const searchResults = response.data.webPages.value;
      const pageData = searchResults.map(result => ({
        title: result.name,
        snippet: result.snippet,
        url: result.url
      })); */
      
      const rankedResults = pageData;
      updateStores({results: rankedResults, isLoadingResults: false, resultsVisible: true});

      let fullAnswer;
      let englishAnswer;
      let answerResponse;
      switch (searchType) {
        case 'advanced':
          answerResponse = await generateAdvancedAnswer(rankedResults, englishQuery, null);
          break;
        case 'professional':
          answerResponse = await generateProAnswer(rankedResults, englishQuery, null);
          break;
        case 'basic':
        default:
          answerResponse = await generateBasicAnswer(rankedResults, englishQuery, null);
          break;
      }

      if (answerResponse && typeof answerResponse === 'object' && answerResponse.fullAnswer) {
        englishAnswer = answerResponse.fullAnswer;
        logCostFromLLMResponse(answerResponse);
      } else {
        englishAnswer = answerResponse;
      }

      if (currentLanguage !== 'en') {
        fullAnswer = await translateFromEnglish(englishAnswer, currentLanguage);
      } else {
        fullAnswer = englishAnswer;
      }

      updateStores({overallSummary: fullAnswer, isLoadingAnswer: false});

      const historyTitleResp = await generateHistoryTitle(query);
      const historyTitle = historyTitleResp.historyTitle;
      logCostFromLLMResponse(historyTitleResp);
      
      let searchThreadId = null;
      if (userIdToUse) { // Only save history if userIdToUse is available
        try {
          console.log('[performSearch] Attempting to save search thread with userId:', userIdToUse);
          const savedThread = await saveSearchThread(userIdToUse, query, rankedResults, fullAnswer, historyTitle);
          searchThreadId = savedThread.ThreadId;
          console.log(`[performSearch] Search thread saved successfully. ThreadId: ${searchThreadId}. Updating history store directly.`);
          const newHistoryItem = {
            ThreadId: savedThread.ThreadId,
            Title: historyTitle,
            Queries: savedThread.Queries || [{ query, results: rankedResults, answer: fullAnswer, timestamp: new Date().toISOString() }],
            createdAt: savedThread.CreatedAt || new Date().toISOString(),
            updatedAt: savedThread.UpdatedAt || new Date().toISOString()
          };
          console.log('[performSearch] Adding newHistoryItem to store:', JSON.stringify(newHistoryItem));
          history.update(currentHistory => [newHistoryItem, ...currentHistory]);
          isHistoryLoading.set(false); // Ensure loading state is false after updating history
          console.log('[performSearch] History store updated directly with new item using local title.');
        } catch (error) {
          console.error('[performSearch] Failed to save search thread or update history store:', error);
        }
      } else {
        console.warn("[performSearch] Skipping save search thread as userId is not available.");
      }

      console.log(`Completed ${searchType} search for query: "${query}"`);
      return {fullAnswer, searchThreadId, rankedResults, historyTitle};
    } else {
      console.warn("No search results found. Response data:", JSON.stringify(response.data, null, 2));
      updateStores({
        results: [],
        overallSummary: "No results found for your query. Please try a different query or check your spelling.",
        isLoadingResults: false,
        isLoadingAnswer: false
      });
      return {fullAnswer: "No results found for your query.", searchThreadId: null, rankedResults: []};
    }
  } catch (error) {
    console.error(`Error in ${searchType} search (performSearch):`, error);
    // ... existing error handling ...
    // Ensure loading states are reset
    isLoadingResults.set(false);
    isLoadingAnswer.set(false);
    resultsVisible.set(false);
    overallSummary.set(error.message || "An error occurred during the search."); // Provide some feedback
    throw error;
  }
}

export async function CheckUserUsage(userId) {
  if (!userId) {
    console.warn('[CheckUserUsage] No userId provided. Skipping user total usage check.');
    localStorage.removeItem('searchUsage'); // Clear any stale usage from localStorage
    // Also clear the userTier from the store and localStorage if no userId
    userTier.set('basic'); // Reset to default or clear as appropriate
    return; // Exit if no userId
  }
  try {
    const totalUsageResponse = await api.post(`/api/search/userTotalUsage`, {userId});
    const basicUsage = totalUsageResponse.data.basicUsage;
    const advancedUsage = totalUsageResponse.data.advancedUsage;
    const professionalUsage = totalUsageResponse.data.professionalUsage;
    const tierName=totalUsageResponse.data.tier;
    console.log('[CheckUserUsage] Total usage received: ', { basicUsage, advancedUsage, professionalUsage });
    console.log('[CheckUserUsage] Tier: ', { tierName });
    userTier.set(tierName || 'basic'); // New way: Update Svelte store, which also updates localStorage

    localStorage.setItem('searchUsage', JSON.stringify({
      basic: basicUsage || 0,
      advanced: advancedUsage || 0,
      professional: professionalUsage || 0
    }));
  } catch (error) {
    console.error('[CheckUserUsage] Error fetching total user usage:', error.response ? error.response.data : error.message);
    // Do not re-throw here if you want the main search to attempt to continue
    // Or re-throw if this check is critical: throw error;
  }
}

export async function performFollowUpSearch(query, passedUserId, searchThreadId, searchType = 'basic') {
  let userIdToUse = passedUserId;

  if (!userIdToUse) {
    const storedData = localStorage.getItem('user');
    if (storedData) {
      try {
        const data = JSON.parse(storedData);
        userIdToUse = data.userId;
        console.log('[performFollowUpSearch] Using userId from localStorage as fallback:', userIdToUse);
      } catch (e) {
        console.error('[performFollowUpSearch] Failed to parse user from localStorage:', e);
      }
    }
  }
  
  if (!userIdToUse && process.env.NODE_ENV === 'development') {
    console.warn('[performFollowUpSearch] userId is not available. User-specific features like usage tracking and history saving will be skipped.');
  } else if (!userIdToUse) {
    console.error('[performFollowUpSearch] CRITICAL: userId is not available.');
  }
  console.log(`[performFollowUpSearch] Effective userId for API calls: ${userIdToUse}, Query: "${query}", Type: ${searchType}, ThreadId: ${searchThreadId}`);

  const currentQueries = get(followUpQueries);
  const queryIndex = currentQueries.length;

  try {
    if (userIdToUse) {
      await getUserUsage(userIdToUse, searchType); // Check usage for the follow-up
      console.log(`[performFollowUpSearch] User usage check completed for userId: ${userIdToUse}`);
    } else {
      console.warn("[performFollowUpSearch] Skipping user usage check as userId is not available.");
    }

    // ... (rest of the performFollowUpSearch logic, ensuring userIdToUse is used for API calls like saveFollowUpQuery)
    // Append the new follow-up query
    followUpQueries.update(Queries => [...Queries, query]);

    // Initialize the new follow-up query's states
    followUpIsLoadingQueryHeader.update(states => [...states, true]);
    followUpIsLoadingResults.update(states => [...states, true]);
    followUpIsLoadingAnswer.update(states => [...states, true]);
    followUpResults.update(resultsArray => [...resultsArray, null]); // Use a different name to avoid conflict
    followUpResultsVisible.update(visibilities => [...visibilities, true]);
    followUpAnswer.update(answers => [...answers, '']);

    await new Promise(resolve => setTimeout(resolve, 500));
    followUpIsLoadingQueryHeader.update(states =>
      states.map((state, index) => index === queryIndex ? false : state)
    );

    const currentThread = get(history).find(item => item.ThreadId === searchThreadId);
    if (!currentThread) {
      console.error("[performFollowUpSearch] Current thread not found. ThreadId:", searchThreadId);
      throw new Error("Unable to find the current search thread.");
    }

    const lastQueryEntry = currentThread.Queries[currentThread.Queries.length - 1];
    const lastQueryText = lastQueryEntry.query;
    // Ensure lastAnswer is correctly extracted, it might be an object
    const lastAnswerText = typeof lastQueryEntry.answer === 'string' ? lastQueryEntry.answer : (lastQueryEntry.answer?.fullAnswer || '');
    const lastAnswerParagraph = getLastParagraph(lastAnswerText);


    const {
      englishQuery,
      englishLastQuery,
      englishLastAnswer,
      currentLanguage
    } = await handleNonEnglishQuery(query, lastQueryText, lastAnswerParagraph);
    const { enhancedQuery, usageData } = await enhanceQueryWithContext(englishQuery, englishLastQuery, englishLastAnswer);

    const simplifiedQuery = simplifyQuery(enhancedQuery);
    const siteRestrictedQuery = buildSiteRestrictedQuery(simplifiedQuery);
    
    const searchTypeSettings = subscriptionConfig.searchTypes[searchType] || subscriptionConfig.searchTypes.basic;
    const freshness = monthsToFreshness(5);
    const bingParams = {
      q: siteRestrictedQuery,
      count: searchTypeSettings.maxSearchResults
    };
    if (freshness) bingParams.freshness = freshness;

    console.groupCollapsed(`🔍 TRACE for follow-up query: ${query}`);
    console.log({ englishQuery, enhancedQuery, simplifiedQuery, siteRestrictedQuery });
    console.log('Final Bing q param ➜', bingParams.q);
    console.groupEnd();

    console.log('Bing Request Params (performFollowUpSearch):', bingParams);

    const response = await api.post('/api/search', bingParams);
    let rankedResults = [];

    //EXA.AI IMPLEMENTATION
    if (response.data.results && response.data.results.length > 0) {
      const searchResultsData = response.data.results; // results de la Exa
      const pageData = searchResultsData.map(result => ({
        title: result.title,
        snippet: result.text 
                  ? result.text.slice(0, 280)     // text trunchiat dacă există
                  : result.summary || "",         // fallback pe summary
        url: result.url
      }));

    /* if (response.data.webPages && response.data.webPages.value && response.data.webPages.value.length > 0) {
      const searchResultsData = response.data.webPages.value; // Renamed to avoid conflict
      const pageData = searchResultsData.map(result => ({
        title: result.name,
        snippet: result.snippet,
        url: result.url
      })); */
      rankedResults = pageData.slice(0, searchTypeSettings.topResults);
      
      followUpResults.update(resultsArray => { // Use different name
        const newResults = [...resultsArray];
        newResults[queryIndex] = rankedResults;
        return newResults;
      });
    } else {
      console.warn("[performFollowUpSearch] No follow-up search results found");
      followUpResults.update(resultsArray => { // Use different name
        const newResults = [...resultsArray];
        newResults[queryIndex] = [];
        return newResults;
      });
    }

    followUpIsLoadingResults.update(states =>
      states.map((state, index) => index === queryIndex ? false : state)
    );

    let fullAnswer;
    // ... (answer generation logic as before)
      switch (searchType) {
        case 'advanced':
          if (currentLanguage !== 'en') {
            const englishAnswer = await generateAdvancedAnswer(rankedResults, englishQuery, {
              parentQuery: englishLastQuery,
              previousAnswer: englishLastAnswer,
              maxTokens: searchTypeSettings.maxTokens,
              includeCitations: true
            });
            fullAnswer = await translateFromEnglish(englishAnswer.fullAnswer, currentLanguage);
          } else {
            fullAnswer = await generateAdvancedAnswer(rankedResults, englishQuery, {
              parentQuery: englishLastQuery,
              previousAnswer: englishLastAnswer,
              maxTokens: searchTypeSettings.maxTokens,
              includeCitations: true
            });
          }
          break;
        // ... other cases ...
        case 'professional': // Added missing professional case from original logic
          if (currentLanguage !== 'en') {
            const englishAnswer = await generateProAnswer(rankedResults, englishQuery, {
              parentQuery: englishLastQuery,
              previousAnswer: englishLastAnswer,
              maxTokens: searchTypeSettings.maxTokens,
              includeCitations: true
            });
            fullAnswer = await translateFromEnglish(englishAnswer.fullAnswer, currentLanguage);
          } else {
            fullAnswer = await generateProAnswer(rankedResults, englishQuery, {
              parentQuery: englishLastQuery,
              previousAnswer: englishLastAnswer,
              maxTokens: searchTypeSettings.maxTokens,
              includeCitations: true
            });
          }
          break;
        case 'basic':
        default:
          if (currentLanguage !== 'en') {
            const englishAnswer = await generateBasicAnswer(rankedResults, englishQuery, {
              parentQuery: englishLastQuery,
              previousAnswer: englishLastAnswer,
              maxTokens: searchTypeSettings.maxTokens,
              includeCitations: true
            });
            fullAnswer = await translateFromEnglish(englishAnswer.fullAnswer, currentLanguage);
          } else {
             fullAnswer  = await generateBasicAnswer(rankedResults, englishQuery, {
              parentQuery: englishLastQuery,
              previousAnswer: englishLastAnswer,
              maxTokens: searchTypeSettings.maxTokens,
              includeCitations: true
            });
          }
          break;
      }


    followUpAnswer.update(answers => {
      const newAnswers = [...answers];
      newAnswers[queryIndex] = fullAnswer.fullAnswer;
      return newAnswers;
    });
    followUpIsLoadingAnswer.update(states =>
      states.map((state, index) => index === queryIndex ? false : state)
    );
    followUpResultsVisible.update(visibilities => {
      const newVisibilities = [...visibilities];
      newVisibilities[queryIndex] = false; // Or true, depending on desired UX
      return newVisibilities;
    });

    if (userIdToUse && searchThreadId) { // Only save if userIdToUse is available
      try {
        console.log(`[performFollowUpSearch] Attempting to save follow-up query for thread ${searchThreadId} with userId ${userIdToUse}...`);
        const updatedThread = await saveFollowUpQuery(searchThreadId, query, rankedResults, fullAnswer.fullAnswer); // fullAnswer here might be an object or string
        console.log(`[performFollowUpSearch] Follow-up query saved successfully. Updating history.`);
        history.update(currentHistory => {
          return currentHistory.map(item => {
            if (item.ThreadId === searchThreadId) {
              return {
                ...item,
                Queries: updatedThread.Queries || [],
                updatedAt: updatedThread.UpdatedAt
              };
            }
            return item;
          });
        });
        isHistoryLoading.set(false); // Ensure loading state is false after updating history
      } catch (error) {
        console.error(`[performFollowUpSearch] Failed to save follow-up query or update history for thread ${searchThreadId}:`, error);
      }
    } else {
        console.warn("[performFollowUpSearch] Skipping save follow-up query as userId or searchThreadId is not available.");
    }
    
    const providerModelKeyFollowUp = `openai:gpt-4o-mini`; // Fallback, actual values come from server
    // Ensure fullAnswer is checked for usageData property if it's an object
    const usageDataFromAnswer = (fullAnswer?.usageData !== null) ? fullAnswer.usageData : null;
    if (usageDataFromAnswer) {
        logTotalCost(usageDataFromAnswer, providerModelKeyFollowUp);
    } else {
         console.warn("[performFollowUpSearch] No usage data available in fullAnswer to log total cost.");
    }
    
    if (userIdToUse) {
      await CheckUserUsage(userIdToUse); // Re-check total usage after follow-up
    }

    // Adjust return to handle potential object structure of fullAnswer
    const answerToReturn = (fullAnswer?.fullAnswer !== null) ? fullAnswer.fullAnswer : fullAnswer;
    return {fullAnswer: answerToReturn, searchThreadId: searchThreadId, rankedResults};

  } catch (error) {
    console.error('Error in follow-up search (performFollowUpSearch):', error);
    // ... (existing error handling, ensuring loading states for queryIndex are reset) ...
    followUpIsLoadingResults.update(states => states.map((s, i) => i === queryIndex ? false : s));
    followUpIsLoadingAnswer.update(states => states.map((s, i) => i === queryIndex ? false : s));
    followUpAnswer.update(answers => {
        const newAnswers = [...answers];
        newAnswers[queryIndex] = "Error processing follow-up.";
        return newAnswers;
    });
    throw error;
  }
}

//Get user usage by userId
async function getUserUsage(userId, searchType) {
  if (!userId) {
    console.warn('[getUserUsage] No userId provided. Skipping user usage check for type:', searchType);
    // In dev/mock mode, we might want to allow the search to proceed without this check.
    // In production, this should ideally not happen or be a hard error.
    return; // Allow to proceed without check if userId is missing
  }
  try {
    console.log('[getUserUsage] Checking usage for userId:', userId, 'searchType:', searchType);
    const response = await api.post(`/api/search/userUsage`, {userId, searchType});
    if (response.status !== 201) { // Or whatever success status your API returns
      console.warn('[getUserUsage] Failed to verify user usage, server responded with status:', response.status);
      // Consider throwing an error if usage check failure should block search
      // throw new Error(`Failed to verify user usage: ${response.statusText}`);
    }
    console.log('[getUserUsage] Usage check successful for userId:', userId);
  } catch (error) {
    console.error('[getUserUsage] Error fetching user usage for userId:', userId, error.response ? error.response.data : error.message);
    // If error is 429, it's a usage limit error, which should be handled by the caller.
    // For other errors, you might want to decide if the search can proceed.
    throw error; // Re-throw to be handled by performSearch/performFollowUpSearch
  }
}

export async function loadUserSearchHistory(userId) {
  try {
    const response = await api.get(`/api/search/history/${userId}`);
    const data = response.data;
    console.log('Loaded history data:', data);
    return data.map(thread => {
      // Ensure the object returned matches the structure expected by History.svelte
      return {
        ThreadId: thread.ThreadId, // <-- Change 'threadId' to 'ThreadId'
        Title: thread.Title || (thread.Queries && thread.Queries.length > 0 ? thread.Queries[0].query : 'Untitled Search'), // <-- Change 'title' to 'Title' and add better fallback
        Queries: thread.Queries || [],
        createdAt: thread.CreatedAt,
        updatedAt: thread.UpdatedAt
      };
    });
  } catch (error) {
    console.error('Error loading user search history:', error);
    throw error;
  }
}

export async function deleteSearchThread(threadId) {
  try {
    const response = await api.delete(`/api/search/thread/${threadId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting search thread:', error);
    throw error;
  }
}

async function saveSearchThread(userId, query, results, answer, historyTitle) {
  try {
    const response = await api.post(`/api/search/thread`, {
      userId,
      query,
      results,
      answer,
      historyTitle
    });

    // Axios throws an error for status codes outside 2xx range, so if we reach here, it was successful
    // No need to check response.ok (which doesn't exist in Axios)

    return response.data;
  } catch (error) {
    console.error('Error saving search thread:', error);
    throw error; // Re-throw to be caught by caller
  }
}

async function saveFollowUpQuery(threadId, query, results, answer) {
  try {
    // Assuming axios is configured with interceptors for auth
    const response = await api.post(`/api/search/thread/${threadId}/query`, {
      query,
      results,
      answer
    });
    // Axios automatically throws for non-2xx status codes,
    // and response.data contains the parsed JSON body
    return response.data;
  } catch (error) {
    console.error('Error saving follow-up query:', error.response?.data?.error || error.message);
    // Re-throw a more specific error if possible
    throw new Error(error.response?.data?.error || 'Failed to save follow-up query');
  }
}

async function handleNonEnglishQuery(query, lastQuery, lastAnswer) {
  const currentLanguage = getCurrentLanguage();
  let englishQuery = query;
  let englishLastQuery = lastQuery;
  let englishLastAnswer = lastAnswer;

  if (currentLanguage !== 'en') {
    try {
      //   console.log(`Translating queries from ${currentLanguage} to English`);
      englishQuery = await translateToEnglish(query, currentLanguage);
      if (lastQuery) englishLastQuery = await translateToEnglish(lastQuery, currentLanguage);
      if (lastAnswer) englishLastAnswer = await translateToEnglish(lastAnswer, currentLanguage);
      //   console.log("Translated query:", englishQuery);
    } catch (translationError) {
      console.error("Translation failed, proceeding with original queries:", query, lastQuery);
      englishQuery = query;
      englishLastQuery = lastQuery;
      englishLastAnswer = lastAnswer;
    }
  }

  return {englishQuery, englishLastQuery, englishLastAnswer, currentLanguage};
}

const getLastParagraph = (text) => {
  const paragraphs = text.split('\n\n').filter(p => p.trim());
  return paragraphs[paragraphs.length - 1] || '';
};